import { useRef, useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { useNavigate, Link } from '@tanstack/react-router'
import { ArrowLeft, User, LogOut, Sparkles, Menu } from 'lucide-react'
import { useAppStore, selectSession } from '@/shared/stores/appStore'
import { supabase } from '@/shared/lib/auth'
import { useMyProfile } from '@/features/home/queries'
import { PreferencesControl } from './PreferencesControl'
import { ThemeToggle } from './ThemeToggle'
import { useTranslation } from '@/shared/i18n/useTranslation'
import styles from './AppHeader.module.css'

type AppHeaderProps = {
  title: string
  /** Renders a back button in place of the mobile menu button. */
  onBack?: () => void
  /** Screen-specific controls, placed before the avatar. */
  actions?: React.ReactNode
}

function deriveInitials(session: ReturnType<typeof selectSession>): string {
  if (!session) return '?'
  const fullName: string = (session.user.user_metadata?.['full_name'] as string | undefined) ?? ''
  if (fullName.trim()) {
    return fullName
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('')
  }
  return session.user.email?.[0]?.toUpperCase() ?? '?'
}

/**
 * The one app header. It grew `onBack` and `actions` so the survivor game page
 * could stop shipping its own copy — that duplicate carried a second avatar
 * dropdown and a second logout path, which drifted from this one.
 */
export function AppHeader({ title, onBack, actions }: AppHeaderProps) {
  const { t } = useTranslation()
  const session = useAppStore(selectSession)
  const setSession = useAppStore((s) => s.setSession)
  const setMobileMenuOpen = useAppStore((s) => s.setMobileMenuOpen)
  const navigate = useNavigate()
  const initials = deriveInitials(session)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const { data: profile } = useMyProfile()
  const isFree = !profile?.subscriptionTier || profile.subscriptionTier === 'FREE'

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  async function handleLogout() {
    setOpen(false)
    await supabase.auth.signOut()
    setSession(null)
    void navigate({ to: '/entrar', search: { redirect: undefined, code: undefined, error: undefined } })
  }

  function handleProfile() {
    setOpen(false)
    void navigate({ to: '/profile' })
  }

  return (
    <header className={styles.header}>
      {onBack ? (
        <button
          type="button"
          className={clsx(styles.menuButton, styles.backButton)}
          onClick={onBack}
          aria-label={t('common.back')}
        >
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
      ) : (
        /* Mobile-only hamburger — opens the sidebar drawer. Hidden at 1024px+. */
        <button
          type="button"
          className={styles.menuButton}
          onClick={() => setMobileMenuOpen(true)}
          aria-label={t('nav.openMenu')}
        >
          <Menu size={22} aria-hidden="true" />
        </button>
      )}

      <h1 className={styles.title}>{title}</h1>

      {actions && <div className={styles.actions}>{actions}</div>}

      <ThemeToggle />

      <div className={styles.avatarWrap} ref={wrapRef}>
        <button
          className={styles.avatar}
          onClick={() => setOpen((v) => !v)}
          aria-label={t('nav.userMenu')}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          {initials}
          {isFree && <span className={styles.tierDot} aria-hidden="true" />}
        </button>

        {open && (
          <div className={styles.dropdown} role="menu">
            <div className={styles.dropdownEmail}>
              {session?.user.email}
            </div>
            <div className={styles.dropdownDivider} />
            {isFree && (
              <Link
                to="/subscription"
                className={styles.dropdownItem}
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <Sparkles size={15} />
                {t('nav.plans')}
              </Link>
            )}
            <button
              className={styles.dropdownItem}
              role="menuitem"
              onClick={handleProfile}
            >
              <User size={15} />
              {t('nav.profile')}
            </button>
            <div className={styles.dropdownDivider} />
            <PreferencesControl isCompact />
            <div className={styles.dropdownDivider} />
            <button
              className={styles.dropdownItem}
              role="menuitem"
              onClick={handleLogout}
            >
              <LogOut size={15} />
              {t('nav.logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
